/**
 * Bijlagen van een mail meenemen als je die mail bij een project bewaart (Joris 18 sep 2026:
 * "als ik die e-mail toevoeg wil ik ook de bijlage analyseren en toevoegen").
 * - Leest de bijlagen via Graph (eigen mailbox, delegated).
 * - Document-bijlagen (docx/pdf/pptx/xlsx/txt/md/csv, ≤ 25 MB): tekst eruit via /Api/parse-attachment
 *   en het bestand zelf naar Documenten van het project (fase wordt geraden op de naam).
 * - Inline plaatjes en handtekeninglogo's slaan we over.
 * Geeft een tekstblok terug om aan de notitie te hangen, plus wat er gebeurd is.
 */
import { BACKEND_URL } from './apiConfig';
import { authFetch } from './authHelpers';
import { listMailAttachments, getMailAttachmentFile, MAX_BIJLAGE_BYTES } from './graphMailSearch';

const DOC_EXT = /\.(docx?|pdf|pptx?|xlsx?|txt|md|csv|rtf)$/i;
const MAX_TEKST_PER_BIJLAGE = 12000;

export function isDocumentBijlage(att) {
  if (!att || att.isInline) return false;
  if (att.size > MAX_BIJLAGE_BYTES) return false;
  if (/^image\//i.test(att.contentType || '')) return false;
  return DOC_EXT.test(att.name || '');
}

// `gezien` (optioneel, een Set): inhoud (sha256) die in deze ronde al verwerkt is. In een
// mailwisseling hangt dezelfde bijlage vaak aan meerdere berichten; "Hele gesprek als
// notitie" zette hem daardoor drie keer bij Documenten (9785, wiki 26 sep). Bewust op
// INHOUD, niet op naam: een aangepaste versie met dezelfde naam moet wél mee.
// Zonder WebCrypto → geen sleutel → niets overslaan (de engine ontdubbelt ook op hash).
export async function inhoudSleutel(file) {
  try {
    const subtle = (typeof window !== 'undefined' ? window.crypto : null)?.subtle;
    if (!subtle || !file?.arrayBuffer) return null;
    const digest = await subtle.digest('SHA-256', await file.arrayBuffer());
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch { return null; }
}

export async function verwerkMailBijlagen(messageId, { projectId, projectNumber, gezien } = {}) {
  let lijst = [];
  try { lijst = await listMailAttachments(messageId); } catch { return { blok: '', bijlagen: [], overgeslagen: 0, fouten: [], lijstFout: true }; }
  const docs = lijst.filter(isDocumentBijlage);
  let dubbel = 0;
  const teGroot = lijst.filter((a) => !a.isInline && DOC_EXT.test(a.name || '') && a.size > MAX_BIJLAGE_BYTES).map((a) => a.name);
  const resultaten = [];
  for (const att of docs) { // eslint-disable-line no-restricted-syntax
    const uit = { naam: att.name, tekst: '', document: false, fout: null };
    try {
      const file = await getMailAttachmentFile(messageId, att.id); // eslint-disable-line no-await-in-loop
      if (gezien) {
        const k = await inhoudSleutel(file); // eslint-disable-line no-await-in-loop
        if (k && gezien.has(k)) { dubbel += 1; continue; } // eslint-disable-line no-continue
        if (k) gezien.add(k);
      }
      const fd = new FormData();
      fd.append('file', file, file.name);
      const r = await authFetch(`${BACKEND_URL}/Api/parse-attachment`, { method: 'POST', body: fd }); // eslint-disable-line no-await-in-loop
      const d = await r.json().catch(() => ({})); // eslint-disable-line no-await-in-loop
      if (r.ok) uit.tekst = String(d.text || '').trim().slice(0, MAX_TEKST_PER_BIJLAGE);
      else uit.fout = d.error || `tekst lezen mislukt (${r.status})`;
      if (projectId) {
        const fd2 = new FormData();
        fd2.append('file', file, file.name);
        fd2.append('fase', '');
        fd2.append('naam', file.name.replace(/\.[^.]+$/, ''));
        if (projectNumber) fd2.append('projectNumber', String(projectNumber));
        const u = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectId}/documents`, { method: 'POST', body: fd2 }); // eslint-disable-line no-await-in-loop
        uit.document = u.ok;
      }
    } catch (e) { uit.fout = e.message; }
    resultaten.push(uit);
  }
  const blok = resultaten.map((b) => (
    `— Bijlage: ${b.naam}${b.document ? ' (bewaard bij Documenten)' : ''} —\n${b.tekst || (b.fout ? `(tekst niet leesbaar: ${b.fout})` : '(geen tekst gevonden)')}`
  )).join('\n\n');
  const fouten = resultaten.filter((b) => b.fout || (projectId && !b.document)).map((b) => b.naam);
  return { blok, bijlagen: resultaten, overgeslagen: lijst.length - docs.length + dubbel, fouten, teGroot, lijstFout: false };
}

/** Waarschuwingstekst als niet alle bijlagen goed meekwamen — de aanroeper toont hem naast het succes. */
export function bijlagenMelding(res) {
  if (!res) return null;
  if (res.lijstFout) return 'Bijlagen konden niet worden opgehaald — de notitie is zonder bijlagen bewaard.';
  const delen = [];
  if ((res.fouten || []).length) delen.push(`niet goed meegekomen: ${res.fouten.join(', ')}`);
  if ((res.teGroot || []).length) delen.push(`te groot (> 25 MB), overgeslagen: ${res.teGroot.join(', ')}`);
  return delen.length ? `Bijlagen ${delen.join('; ')}. Voeg ze zo nodig los toe bij Documenten.` : null;
}
